import React, { useState, useEffect } from "react";
import style from "./style.module.scss";
import PlanSummary from "./PlanSummary";
import IcLevelsTable from "./IcLevelsTable";
import CardBrandAssessments from "./CardBrandAssessments";
import ResidualCalculationsTable from "./ResidualCalculations";
import HiddenDataTable from "../../../../components/HiddenDataTable";
import useCommonStore from "../../../../stores/common";
import useMerchantStore from "../../../../stores/merchant";
import useUserStore from "../../../../stores/user";
import { getMerchantResidualsDetail } from "../../../../../api";
import toast from "react-hot-toast";

const ResidualsMessage = ({ children }) => (
  <div className={style.residuals_section}>
    <div className={style.section_card}>
      <div className={style.section_header}>
        <div className={style.section_title}>
          <h5>Residuals</h5>
        </div>
      </div>
      <div className={style.section_body}>
        <p className={style.empty_message}>{children}</p>
      </div>
    </div>
  </div>
);

function Residuals() {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const activeApp = useMerchantStore((state) => state.activeApp);
  const dateRange = useCommonStore((state) => state.dateRange);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isMultiMonthPreset = ["YTD", "LTY"].includes(dateRange?.rangeType);

  const [planSummary, setPlanSummary] = useState({});
  const [baseRates, setBaseRates] = useState([]);
  const [icLevelsData, setIcLevelsData] = useState([]);
  const [assessmentsData, setAssessmentsData] = useState([]);
  const [calculationsData, setCalculationsData] = useState([]);
  const [calculationsResidualAmount, setCalculationsResidualAmount] =
    useState(null);
  const [calculationsNeedsSingleMonth, setCalculationsNeedsSingleMonth] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!activeApp?.power_app_submission?.mid || !activeApp?.id) {
      setPlanSummary({});
      setBaseRates([]);
      setIcLevelsData([]);
      setAssessmentsData([]);
      setCalculationsData([]);
      setCalculationsResidualAmount(null);
      setCalculationsNeedsSingleMonth(false);
      setAccessDenied(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setAccessDenied(false);
      try {
        const response = await getMerchantResidualsDetail({
          powerapp_id: activeApp.id,
          start_date: dateRange?.startDate,
          end_date: dateRange?.endDate,
        });
        if (cancelled) {
          return;
        }
        if (response?.status && response?.data) {
          setPlanSummary(response.data.plan_summary ?? {});
          setBaseRates(response.data.base_rates ?? []);
          setIcLevelsData(response.data.ic_levels ?? []);
          setAssessmentsData(response.data.card_brand_assessments ?? []);
          setCalculationsData(response.data.residual_calculations?.rows ?? []);
          setCalculationsResidualAmount(
            response.data.residual_calculations?.residual_amount ?? null,
          );
          setCalculationsNeedsSingleMonth(
            response.data.residual_calculations?.requires_single_month === true,
          );
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setPlanSummary({});
        setBaseRates([]);
        setIcLevelsData([]);
        setAssessmentsData([]);
        setCalculationsData([]);
        setCalculationsResidualAmount(null);
        setCalculationsNeedsSingleMonth(false);

        // A denial gets the panel below rather than a toast, so the section
        // never sits there looking like a merchant with no residual activity.
        if (error?.status === 403) {
          setAccessDenied(true);
          return;
        }

        const msg =
          error?.data?.message ??
          error?.data?.error ??
          "Failed to load residuals.";
        toast.error(msg);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    activeApp?.power_app_submission?.mid,
    activeApp?.id,
    dateRange?.startDate,
    dateRange?.endDate,
  ]);

  if (!expandDetails) {
    return (
      <div className={style.section_card}>
        <HiddenDataTable />
      </div>
    );
  }

  if (accessDenied) {
    return <ResidualsMessage>You do not have access</ResidualsMessage>;
  }

  if (!activeApp?.power_app_submission?.mid) {
    return (
      <ResidualsMessage>
        Residuals are available once this application has a Merchant ID (MID).
      </ResidualsMessage>
    );
  }

  return (
    <div className={style.residuals_section}>
      <PlanSummary
        planSummary={planSummary}
        baseRates={baseRates}
        loading={loading}
      />

      <div className={style.section_card}>
        <div className={style.section_header}>
          <div className={style.section_title}>
            <h5>IC Levels</h5>
          </div>
        </div>
        <div className={style.section_body}>
          <IcLevelsTable data={icLevelsData} loading={loading} />
        </div>
      </div>

      <div className={style.section_card}>
        <div className={style.section_header}>
          <div className={style.section_title}>
            <h5>Card Brand Assessments</h5>
          </div>
        </div>
        <div className={style.section_body}>
          <CardBrandAssessments data={assessmentsData} loading={loading} />
        </div>
      </div>

      {isAdmin && (
        <div className={style.section_card}>
          <div className={style.section_header}>
            <div className={style.section_title}>
              <h5>Residual Calculations</h5>
            </div>
          </div>
          <div className={style.section_body}>
            <ResidualCalculationsTable
              data={calculationsData}
              residualAmount={calculationsResidualAmount}
              requiresSingleMonth={
                isMultiMonthPreset || calculationsNeedsSingleMonth
              }
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Residuals;
